/**
 * The ops session: access token in memory, refresh token in localStorage with
 * an explicit expiry.
 *
 * ── This REVERSES an earlier decision, deliberately ────────────────────────
 *
 * This file used to hold the refresh token in `sessionStorage`, and argued for
 * it: a refresh token is a thirty-day credential to a dashboard that reads every
 * merchant's data and can mint impersonation tokens, `sessionStorage` dies with
 * the tab, and re-logging in was the right price for that particular door.
 *
 * The owner asked for persistence instead — one week on production, one month on
 * dev. That is their call to make. What changes the risk calculus is the second
 * half of it: the stored record carries its own `expiresAt`, checked on every
 * read, so the credential is bounded rather than living until someone clears
 * their browser. The original objection was "survives until someone clears it",
 * and that specific property is gone.
 *
 * ⚠ What is NOT mitigated, stated plainly: on a shared or borrowed machine the
 * session now outlives the tab. The passkey still gates a *new* sign-in, but an
 * existing one is resumable by whoever has the machine, for up to the window
 * below.
 *
 * ── The expiry does not slide ──────────────────────────────────────────────
 *
 * The window is measured from LOGIN, not from last use. A refresh response that
 * rotates the token keeps the original `expiresAt`, so "one week" means one week
 * — not one week after you last happened to open it, which is a window that
 * never closes for anyone who uses the dashboard weekly.
 *
 * The ACCESS token is still never persisted. It lives in a module variable and
 * is re-minted from the refresh token on load.
 */

const REFRESH_KEY = 'confetto_ops_refresh';
/** The pre-2026-09-06 location. Cleared on load so a stale copy cannot linger. */
const LEGACY_SESSION_KEY = 'confetto_ops_refresh';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * How long a signed-in session survives.
 *
 * ⚠ Production is the SHORTER one. Dev is a machine with test data on it;
 * production reaches every real merchant's shop record.
 *
 * `import.meta.env.MODE` is the `--mode` the bundle was built with, so this is
 * decided at build time by `build:dev` / `build:prod` — the same pairing the
 * API base URL depends on, and the one `check-env-baked.mjs` verifies.
 */
const TTL_MS = import.meta.env.MODE === 'prod' ? 7 * DAY_MS : 30 * DAY_MS;

interface StoredSession {
  token: string;
  /** Epoch ms. Absolute, set once at login and preserved across rotations. */
  expiresAt: number;
}

let accessToken: string | null = null;
let expiresAt = 0;

export interface AdminInfo {
  id: number;
  username: string;
  displayName: string | null;
}

let admin: AdminInfo | null = null;

export const getAdmin = () => admin;
export const setAdmin = (a: AdminInfo | null) => {
  admin = a;
};

function readStored(): StoredSession | null {
  try {
    const raw = localStorage.getItem(REFRESH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (typeof parsed?.token !== 'string' || typeof parsed?.expiresAt !== 'number') {
      // Malformed — treat as absent rather than throwing on every page load.
      localStorage.removeItem(REFRESH_KEY);
      return null;
    }
    if (Date.now() >= parsed.expiresAt) {
      localStorage.removeItem(REFRESH_KEY);
      return null;
    }
    return { token: parsed.token, expiresAt: parsed.expiresAt };
  } catch {
    return null;
  }
}

export function setSession(access: string, expiresIn: number, refresh?: string): void {
  accessToken = access;
  // Refresh a minute early, so a request never races its own expiry.
  expiresAt = Date.now() + (expiresIn - 60) * 1000;
  if (!refresh) return;
  try {
    /**
     * ⚠ Keep the EXISTING expiry if there is one. This runs on every token
     * rotation, and stamping a fresh `Date.now() + TTL_MS` each time would turn
     * the window into a sliding one that never closes for a regular user.
     */
    const existing = readStored();
    const record: StoredSession = {
      token: refresh,
      expiresAt: existing?.expiresAt ?? Date.now() + TTL_MS,
    };
    localStorage.setItem(REFRESH_KEY, JSON.stringify(record));
  } catch {
    // Private mode, or storage disabled. The session still works for this page
    // load; it just will not survive a reload.
  }
}

export function clearSession(): void {
  accessToken = null;
  expiresAt = 0;
  admin = null;
  try {
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    // Nothing to clear.
  }
  try {
    // ⚠ A signing-out user expects EVERY copy gone, including the old location.
    sessionStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * ⚠ Drop any token left in the old `sessionStorage` slot.
 *
 * Nothing reads it any more, so it would sit there as an unreachable credential
 * that a signing-out user was never able to clear. Runs once, at import.
 */
try {
  sessionStorage.removeItem(LEGACY_SESSION_KEY);
} catch {
  // Nothing to clear.
}

export const getRefreshToken = (): string | null => readStored()?.token ?? null;

export const currentAccessToken = (): string | null =>
  accessToken && Date.now() < expiresAt ? accessToken : null;

export const hasStoredSession = (): boolean => !!getRefreshToken();

/** Epoch ms the stored session lapses, or null when there is none. */
export const sessionExpiresAt = (): number | null => readStored()?.expiresAt ?? null;
